import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Plus, ClipboardList, Check, AlertTriangle } from "lucide-react";
import { todayPanelTz, type Standup } from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

interface StandupRow {
  standup: Standup;
  authorName: string;
  authorEmail: string;
}

export const Route = createFileRoute("/standups/")({
  component: StandupsPage,
});

function StandupsPage() {
  const { data, isLoading } = useQuery<{ standups: StandupRow[] }>({
    queryKey: ["standups"],
    queryFn: () => api.get("/standups"),
  });

  const today = useQuery<{ standup: Standup | null }>({
    queryKey: ["standup", "me", "today"],
    queryFn: () => api.get("/standups/me/today"),
  });

  // Partición por fecha. "Hoy" = todo lo que se posteó con date===today
  // (los standups del equipo del día actual). "Pasados" = el resto.
  // Calendar-aligned, no relative — si alguien posta un standup
  // "de ayer" hoy, aparece en Pasados, no en Hoy.
  const todayIso = todayPanelTz();
  const { todayRows, pastRows } = useMemo(() => {
    const rows = data?.standups ?? [];
    const tr: StandupRow[] = [];
    const pr: StandupRow[] = [];
    for (const r of rows) {
      if (r.standup.date === todayIso) tr.push(r);
      else pr.push(r);
    }
    return { todayRows: tr, pastRows: pr };
  }, [data, todayIso]);

  const [scope, setScope] = useState<"today" | "past">("today");
  const visibleRows = scope === "today" ? todayRows : pastRows;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="standups"
        title="Standups del equipo"
        subtitle="Lo que hizo cada quién ayer y planea hoy. La transparencia es parte de la metodología — todos pueden leer."
        actions={
          today.data?.standup ? (
            <Link
              to="/standups/$standupId"
              params={{ standupId: today.data.standup.id }}
              className="btn btn-ghost"
            >
              <Check className="size-4 text-success" /> Mi standup de hoy
            </Link>
          ) : (
            <Link to="/standups/new" className="btn btn-primary">
              <Plus className="size-4" /> Hacer standup
            </Link>
          )
        }
      />

      {!today.isLoading && !today.data?.standup && (
        <div className="card-elevated p-4 flex items-start gap-3 border-l-4 border-l-warning">
          <AlertTriangle className="size-5 text-warning shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium">No has hecho tu standup hoy.</div>
            <div className="text-sm text-base-content/65 mt-0.5">
              Toma 2 minutos para que el equipo sepa en qué andas.
            </div>
          </div>
          <Link to="/standups/new" className="btn btn-primary btn-sm">
            Hacerlo ahora
          </Link>
        </div>
      )}

      {isLoading && (
        <div className="grid place-items-center py-20">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      )}

      {data && data.standups.length === 0 && (
        <div className="card-elevated py-16 text-center">
          <div className="size-14 rounded-full bg-primary/10 grid place-items-center mx-auto mb-4">
            <ClipboardList className="size-6 text-primary" />
          </div>
          <h3 className="font-display font-semibold text-base-content mb-1">Aún no hay standups</h3>
          <p className="text-sm text-base-content/60 mb-5">Sé el primero en hacer uno.</p>
          <Link to="/standups/new" className="btn btn-primary">
            Hacer standup
          </Link>
        </div>
      )}

      {data && data.standups.length > 0 && (
        <div
          role="tablist"
          className="grid grid-cols-2 gap-0 rounded-xl overflow-hidden border border-base-300/60 bg-base-200/30"
        >
          {(
            [
              { v: "today", l: "Hoy", count: todayRows.length },
              { v: "past", l: "Pasados", count: pastRows.length },
            ] as { v: "today" | "past"; l: string; count: number }[]
          ).map((opt) => {
            const active = scope === opt.v;
            return (
              <button
                key={opt.v}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setScope(opt.v)}
                className={
                  "flex items-center justify-center gap-2.5 px-4 py-3.5 text-sm font-semibold transition-all " +
                  (active
                    ? "bg-primary text-primary-content shadow-inner"
                    : "text-base-content/60 hover:text-base-content hover:bg-base-200/60")
                }
              >
                {opt.l}
                <span
                  className={
                    "text-xs font-bold tabular-nums px-2 py-0.5 rounded-full " +
                    (active
                      ? "bg-primary-content/20 text-primary-content"
                      : "bg-base-300/60 text-base-content/70")
                  }
                >
                  {opt.count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {data && data.standups.length > 0 && visibleRows.length === 0 && (
        <div className="card-elevated py-12 text-center text-sm text-base-content/55">
          {scope === "today" ? "Nadie ha posteado standup hoy todavía." : "Sin standups pasados."}
        </div>
      )}

      <div className="space-y-2">
        {visibleRows.map((row) => (
          <Link
            key={row.standup.id}
            to="/standups/$standupId"
            params={{ standupId: row.standup.id }}
            className="card-elevated p-4 flex items-start gap-4 hover:[&]:card-elevated-hover"
          >
            <div className="size-10 rounded-full bg-primary/10 grid place-items-center text-primary font-semibold uppercase shrink-0">
              {row.authorName
                .split(" ")
                .slice(0, 2)
                .map((p) => p[0])
                .join("")}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-3">
                <div className="font-medium truncate">{row.authorName}</div>
                <div className="text-xs text-base-content/55 shrink-0">
                  {formatDate(row.standup.date)}
                </div>
              </div>
              {row.standup.comments && row.standup.comments.trim() && (
                <p className="text-sm text-base-content/70 line-clamp-2 mt-1 leading-relaxed">
                  <span className="font-medium text-base-content">Comentarios:</span>{" "}
                  {row.standup.comments}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
